import React, { useEffect, useMemo, useState } from 'react';

import { createSearchParams, useSearchParams } from 'react-router-dom';
import dayjs from '~/timeInit';

import { Alert, Box, Button, Card, CardContent, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import type { DateRange } from '@mui/x-date-pickers-pro';

import SearchBigQueryDateRangePicker, {
  getDefaultSearchDateRange,
  toBigQueryExclusiveEndDate,
} from '~/components/Content/SearchAnalytics/SearchBigQueryDateRangePicker';

import { useGetAnalyticsSearchCounts } from '~/hooks/apis/analytics/useGetAnalyticsSearchCounts';
import { useGetAnalyticsSearchStats } from '~/hooks/apis/analytics/useGetAnalyticsSearchStats';

import type { AnalyticsSearchStatsParams } from '~/services/AnalyticsService';
import type { Dayjs } from '~/services/TimeService';

import AnalyticsExplainerAlert from './AnalyticsExplainerAlert';
import GroupedSearchTable from './GroupedSearchTable';
import SearchCategorySection from './SearchCategorySection';
import SearchCountsBar from './SearchCountsBar';
import SuggestionQueryConfigDialog from './SuggestionQueryConfigDialog';
import { type CategorisedSearchGroups, categoriseSearchGroups } from './categoriseSearchGroups';
import { groupSearchStatsByQuery } from './groupSearchStats';
import { useSuggestionQueryConfig } from './useSuggestionQueryConfig';

const DEFAULT_SEARCH_STATS_LIMIT = 100;
const MIN_SEARCH_STATS_LIMIT = 1;
const MAX_SEARCH_STATS_LIMIT = 100000;

const SEARCH_CATEGORY_SECTIONS = [
  { key: 'other', title: 'NLS Searches' },
  { key: 'prefixMatched', title: 'Place/Property searches' },
  { key: 'suggestions', title: 'NLS Suggestions' },
] as const satisfies ReadonlyArray<{ key: keyof CategorisedSearchGroups; title: string }>;

function isValidLimit(value: number): boolean {
  return Number.isInteger(value) && value >= MIN_SEARCH_STATS_LIMIT && value <= MAX_SEARCH_STATS_LIMIT;
}

function parseLimitParam(value: string | null): number | null {
  if (value === null) {
    return DEFAULT_SEARCH_STATS_LIMIT;
  }

  const parsed = Number(value);
  return isValidLimit(parsed) ? parsed : null;
}

function parseAnalyticsUrlState(searchParams: URLSearchParams): {
  dateRange: DateRange<Dayjs>;
  limit: number;
  submittedParams: AnalyticsSearchStatsParams;
} | null {
  const startDateStr = searchParams.get('startDate');
  const endDateStr = searchParams.get('endDate');

  if (!startDateStr || !endDateStr) {
    return null;
  }

  const start = dayjs(startDateStr);
  const end = dayjs(endDateStr);

  if (!start.isValid() || !end.isValid()) {
    return null;
  }

  const limit = parseLimitParam(searchParams.get('limit'));
  if (limit === null) {
    return null;
  }

  const startOfDay = start.startOf('day');
  const endOfDay = end.endOf('day');

  return {
    dateRange: [startOfDay, endOfDay],
    limit,
    submittedParams: {
      startDate: startOfDay.format('YYYY-MM-DD'),
      endDate: toBigQueryExclusiveEndDate(endOfDay),
      limit,
    },
  };
}

export default function AnalyticsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [dateRange, setDateRange] = useState<DateRange<Dayjs>>(getDefaultSearchDateRange);
  const [limit, setLimit] = useState(DEFAULT_SEARCH_STATS_LIMIT);

  const urlState = useMemo(() => parseAnalyticsUrlState(searchParams), [searchParams]);
  const submittedParams = urlState?.submittedParams ?? null;

  const [startDate, endDate] = dateRange;
  const {
    data: countsData,
    isFetching: isLoadingCounts,
    error: countsError,
    refetch: refetchCounts,
  } = useGetAnalyticsSearchCounts(submittedParams);
  const {
    data,
    isFetching: isLoadingResults,
    error,
    refetch: refetchStats,
  } = useGetAnalyticsSearchStats(submittedParams);
  const { config: suggestionConfig, setConfig, resetConfig, isCustomised } = useSuggestionQueryConfig();
  const [isSuggestionConfigOpen, setIsSuggestionConfigOpen] = useState(false);
  const groups = useMemo(() => groupSearchStatsByQuery(data ?? []), [data]);
  const categorised = useMemo(() => categoriseSearchGroups(groups, suggestionConfig), [groups, suggestionConfig]);
  const showResults = Boolean(data) && !isLoadingResults;

  const isInputComplete = Boolean(startDate && endDate && isValidLimit(limit));

  useEffect(() => {
    if (!urlState) {
      return;
    }

    setDateRange(urlState.dateRange);
    setLimit(urlState.limit);
  }, [urlState]);

  const handleLimitChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextLimit = event.target.valueAsNumber;
    setLimit(Number.isNaN(nextLimit) ? MIN_SEARCH_STATS_LIMIT : nextLimit);
  };

  const handleRunQuery = () => {
    if (!startDate || !endDate || !isValidLimit(limit)) {
      return;
    }

    const nextSearchParams = createSearchParams({
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD'),
      limit: String(limit),
    });
    const isRetryWithSameParams = ['startDate', 'endDate', 'limit'].every(
      (key) => searchParams.get(key) === nextSearchParams.get(key),
    );

    setSearchParams(nextSearchParams);

    if (isRetryWithSameParams) {
      void refetchStats();
      void refetchCounts();
    }
  };

  return (
    <Stack spacing={0}>
      <SearchBigQueryDateRangePicker dateRange={dateRange} onChange={setDateRange}>
        <Box
          sx={{
            display: 'flex',
            flexGrow: 1,
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            alignItems: { md: 'center' },
          }}
        >
          <TextField
            label="Number of searches"
            type="number"
            value={limit}
            onChange={handleLimitChange}
            size="small"
            required
            error={!isValidLimit(limit)}
            helperText={
              !isValidLimit(limit)
                ? `Enter a value between ${MIN_SEARCH_STATS_LIMIT} and ${MAX_SEARCH_STATS_LIMIT}`
                : undefined
            }
            inputProps={{ min: MIN_SEARCH_STATS_LIMIT, max: MAX_SEARCH_STATS_LIMIT, step: 1 }}
            sx={{ width: { md: 180 } }}
          />
          <Stack direction="row" spacing={1} sx={{ ml: { md: 'auto' } }}>
            <Button variant="outlined" color="inherit" onClick={() => setIsSuggestionConfigOpen(true)}>
              Suggestion rules
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleRunQuery}
              disabled={isLoadingResults || isLoadingCounts || !isInputComplete}
            >
              Run query
            </Button>
          </Stack>
        </Box>
      </SearchBigQueryDateRangePicker>

      <AnalyticsExplainerAlert />

      {submittedParams && <SearchCountsBar data={countsData} isLoading={isLoadingCounts} error={countsError} />}

      {(isLoadingResults || error) && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            {isLoadingResults && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 3,
                  gap: 2,
                }}
              >
                <CircularProgress size={32} />
                <Typography variant="body2">Fetching stats from BigQuery</Typography>
              </Box>
            )}

            {!isLoadingResults && error && (
              <Alert severity="error">{error instanceof Error ? error.message : 'Failed to fetch analytics'}</Alert>
            )}
          </CardContent>
        </Card>
      )}

      {showResults && submittedParams && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            {groups.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No searches found for the selected range
              </Typography>
            ) : (
              <Stack spacing={3}>
                {SEARCH_CATEGORY_SECTIONS.map((section) => {
                  const sectionGroups = categorised[section.key];
                  const sectionSearchCount = sectionGroups.reduce((total, group) => total + group.count, 0);

                  return (
                    <SearchCategorySection key={section.key} title={section.title} count={sectionSearchCount}>
                      {sectionGroups.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No searches in this category
                        </Typography>
                      ) : (
                        <GroupedSearchTable groups={sectionGroups} dateRange={submittedParams} />
                      )}
                    </SearchCategorySection>
                  );
                })}
              </Stack>
            )}
          </CardContent>
        </Card>
      )}
      <SuggestionQueryConfigDialog
        open={isSuggestionConfigOpen}
        onClose={() => setIsSuggestionConfigOpen(false)}
        config={suggestionConfig}
        isCustomised={isCustomised}
        onChange={setConfig}
        onReset={resetConfig}
      />
    </Stack>
  );
}
